// @ts-check
// Estilo visual elegido por el alumno (rediseño, REDISENO-WEB.md, Fase 2c):
// `arcade` (por defecto), `cuaderno` o `bloques`.
//
// Sustituye a los estilos clásico y plano (DEC-103) Y al tema claro/oscuro
// (DEC-044): cada estilo decide su propio modo (Arcade es oscuro; Cuaderno y
// Bloques, claros), así que ya no hay un segundo eje que combinar. Se aplica
// como `data-estilo` en `<html>` y los tokens se redefinen bajo él en
// `styles/tokens.css`.
//
// ## Por qué solo en el navegador, y no en la base de datos
//
// Decisión del usuario al empezar el rediseño (27 sep 2026), la misma que ya
// tomó DEC-103: es una preferencia estética y no justifica una migración de
// la base de datos. En otro ordenador vuelve al estilo por defecto, y la
// pantalla de Apariencia lo dice.
//
// ## Sin parpadeo
//
// Las páginas arrancan ya con `data-estilo="arcade"` en el HTML, y
// `public/estilo-inicial.js` -- un script diminuto y bloqueante del `<head>` --
// pone el estilo guardado ANTES del primer pintado. Ese script no puede ser un
// módulo (los módulos se ejecutan tarde), así que repite en pequeño
// `resolverEstiloInicial`; la copia está atada por
// `tests/unit/state/estilo-inicial.spec.js`.
//
// ## Migración desde las preferencias antiguas (decidida el 27 sep 2026)
//
// Quien había elegido A MANO el tema CLARO pasa a Cuaderno, el estilo claro
// más parecido; todos los demás, a Arcade. Pisar una preferencia puesta a
// mano se siente como un fallo de la aplicación, no como un cambio de diseño.
// - En este navegador: la copia local del tema (`tema-cache-v1`), leída la
//   primera vez y luego borrada junto con `estilo-cache-v1`.
// - Desde la cuenta: `preferenciaTema` de `/auth/me` (`migrarDesdeCuenta`),
//   para quien entra por primera vez desde otro ordenador.
// Solo actúa mientras el alumno no haya elegido estilo en este navegador: en
// cuanto elige, su elección manda.
//
// ## Fallo seguro
//
// Cualquier valor desconocido, `null`, o un `localStorage` no disponible
// (modo privado, cuotas, políticas del navegador) resuelven a `arcade`.
// Nunca se deja el documento en un estado sin estilo resuelto.

/** Clave del estilo en `localStorage`. v2: los valores de v1 ya no existen. */
export const CLAVE_CACHE_ESTILO = 'estilo-cache-v2';

/** Claves de antes del rediseño: se leen una vez para migrar y se borran. */
export const CLAVE_ESTILO_ANTIGUA = 'estilo-cache-v1';
export const CLAVE_TEMA_ANTIGUA = 'tema-cache-v1';

/** Estilo con el que arranca quien nunca ha elegido. */
export const ESTILO_POR_DEFECTO = 'arcade';

/** Estilo para quien había elegido a mano el tema claro. */
export const ESTILO_PARA_TEMA_CLARO = 'cuaderno';

/** Los estilos que existen. */
export const ESTILOS = /** @type {const} */ (['arcade', 'cuaderno', 'bloques']);

/** @typedef {(typeof ESTILOS)[number]} EstiloVisual */

/**
 * ¿Es un valor de estilo reconocido?
 * @param {unknown} valor
 * @returns {valor is EstiloVisual}
 */
export function esEstiloValido(valor) {
  return typeof valor === 'string' && /** @type {readonly string[]} */ (ESTILOS).includes(valor);
}

/**
 * El almacén, o `null` si el navegador no deja usarlo.
 * @param {Storage | undefined} almacen
 * @returns {Storage | null}
 */
function almacenDe(almacen) {
  try {
    return almacen ?? window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Aplica el estilo al documento escribiendo `data-estilo` en `<html>`.
 * @param {unknown} estilo
 * @param {{ documento?: Document }} [opciones]
 * @returns {EstiloVisual}
 */
export function aplicarEstilo(estilo, opciones = {}) {
  const documento = opciones.documento ?? document;
  const resuelto = esEstiloValido(estilo) ? estilo : ESTILO_POR_DEFECTO;
  documento.documentElement.dataset.estilo = resuelto;
  return resuelto;
}

/**
 * Lee el estilo elegido en este navegador, o `null` si no hay ninguno.
 * @param {{ almacen?: Storage }} [opciones]
 * @returns {EstiloVisual | null}
 */
export function leerEstiloGuardado(opciones = {}) {
  try {
    const valor = almacenDe(opciones.almacen)?.getItem(CLAVE_CACHE_ESTILO);
    return esEstiloValido(valor) ? valor : null;
  } catch {
    return null;
  }
}

/**
 * Guarda el estilo elegido. Absorbe cualquier fallo del almacén.
 * @param {unknown} estilo
 * @param {{ almacen?: Storage }} [opciones]
 * @returns {void}
 */
export function guardarEstilo(estilo, opciones = {}) {
  if (!esEstiloValido(estilo)) return;
  try {
    almacenDe(opciones.almacen)?.setItem(CLAVE_CACHE_ESTILO, estilo);
  } catch {
    // Sin almacén, el estilo dura lo que la pestaña. Es aceptable.
  }
}

/**
 * El estilo con el que debe pintarse la página, SIN escribir nada: el
 * elegido; si no hay, Cuaderno para quien tenía el tema claro en este
 * navegador; si no, el de por defecto. Es exactamente lo que repite
 * `public/estilo-inicial.js` antes del primer pintado.
 * @param {{ almacen?: Storage }} [opciones]
 * @returns {EstiloVisual}
 */
export function resolverEstiloInicial(opciones = {}) {
  const elegido = leerEstiloGuardado(opciones);
  if (elegido) return elegido;
  try {
    if (almacenDe(opciones.almacen)?.getItem(CLAVE_TEMA_ANTIGUA) === 'claro') {
      return ESTILO_PARA_TEMA_CLARO;
    }
  } catch {
    // Sin almacén: el de por defecto.
  }
  return ESTILO_POR_DEFECTO;
}

/**
 * Migra las preferencias de antes del rediseño que haya en este navegador y
 * borra sus claves. Idempotente: la segunda vez no encuentra nada.
 * @param {{ almacen?: Storage }} [opciones]
 * @returns {void}
 */
export function migrarPreferenciasAntiguas(opciones = {}) {
  const almacen = almacenDe(opciones.almacen);
  if (!almacen) return;
  try {
    if (!leerEstiloGuardado(opciones) && almacen.getItem(CLAVE_TEMA_ANTIGUA) === 'claro') {
      guardarEstilo(ESTILO_PARA_TEMA_CLARO, opciones);
    }
    almacen.removeItem(CLAVE_ESTILO_ANTIGUA);
    almacen.removeItem(CLAVE_TEMA_ANTIGUA);
  } catch {
    // Un fallo aquí solo deja las claves viejas: nada que el alumno note.
  }
}

/**
 * Última oportunidad de la migración, con la preferencia de la CUENTA
 * (`/auth/me`): quien eligió el tema claro y entra desde un ordenador donde
 * todavía no ha elegido estilo pasa a Cuaderno. Si ya eligió, no se toca.
 * @param {unknown} preferenciaTema
 * @param {{ documento?: Document, almacen?: Storage }} [opciones]
 * @returns {EstiloVisual | null} El estilo aplicado, o `null` si no hizo nada.
 */
export function migrarDesdeCuenta(preferenciaTema, opciones = {}) {
  if (preferenciaTema !== 'claro' || leerEstiloGuardado(opciones)) return null;
  return cambiarEstilo(ESTILO_PARA_TEMA_CLARO, opciones);
}

/**
 * Migra lo antiguo, lee lo guardado y lo aplica. Es lo que llama el
 * arranque de cada página.
 * @param {{ documento?: Document, almacen?: Storage }} [opciones]
 * @returns {EstiloVisual}
 */
export function restaurarEstilo(opciones = {}) {
  migrarPreferenciasAntiguas(opciones);
  return aplicarEstilo(resolverEstiloInicial(opciones), opciones);
}

/**
 * Cambia al estilo indicado: lo aplica y lo guarda.
 * @param {unknown} estilo
 * @param {{ documento?: Document, almacen?: Storage }} [opciones]
 * @returns {EstiloVisual}
 */
export function cambiarEstilo(estilo, opciones = {}) {
  const resuelto = aplicarEstilo(estilo, opciones);
  guardarEstilo(resuelto, opciones);
  return resuelto;
}
